// =============================================================================
// useConnections — org-scoped store (Connection) list for store filters
// =============================================================================
// Shared by every store-scoped view (orders, products, payments, wallet).
// Returns ALL connections the org owns — including uninstalled/disconnected
// stores — so historical data can still be filtered by them. The UI labels
// non-active stores (see <StoreFilter>) rather than hiding them.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';

export interface ConnectionSummary {
  id: string;
  organizationId: string;
  type: string;
  name: string;
  status: string;
  shopDomain: string | null;
  syncStatus: string;
  lastSyncAt: string | null;
  productCount: number;
  orderCount: number;
  createdAt: string;
}

/**
 * React Query hook for the org's connected stores. Cached under a stable key
 * so the filter on every page shares one fetch. `staleTime` is generous —
 * store lists change rarely (only on install/uninstall).
 */
export function useConnections() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['organization', 'connections'],
    queryFn: () =>
      api.get<ConnectionSummary[]>('/api/v1/organizations/current/connections'),
    staleTime: 5 * 60 * 1000,
  });
}
